import z from "zod";

import { UUID } from "../generic/uuid.ts";

import {
  ElementsState,
  ExtendedElementTypes,
  isContainerElement,
  WireframeElement,
} from "./elements.ts";
import { Dimensions } from "./screen.ts";

export type ScreenState = {
  // id of root element of the screen
  elementId: UUID;
  // contains only elements used in this screen (i.e. reachable from the root element)
  elements: ElementsState;
};

export const getScreenElement = (state: ScreenState): WireframeElement => {
  const element = state.elements[state.elementId];
  if (!element || element.type !== ExtendedElementTypes.Wireframe) {
    // this basically should never happen, as ScreenState should always
    // be constructed with elementId in elements
    throw `Screen element not found by id ${state.elementId}`;
  }
  return element;
};

export const ScreenJsx = z.object({
  dimensions: Dimensions,
  jsx: z.string(),
});
export type ScreenJsx = z.infer<typeof ScreenJsx>;

export type WireframeToJsxSkipProp = (
  propName: string,
  elementId: string,
) => boolean;

export const collectScreenState = (
  elementId: UUID,
  elementsState: ElementsState,
): ScreenState => {
  const newState: ElementsState = {};

  const collect = (id: UUID) => {
    const element = elementsState[id];
    if (element) {
      newState[id] = element;

      if (isContainerElement(element)) {
        element.children.forEach(collect);
      }
    }
  };

  collect(elementId);

  if (!newState[elementId]) {
    throw new Error("Screen-root element not found in elements state");
  }

  return {
    elementId,
    elements: newState,
  };
};
